import { offlineQueue, QueueItem, QueueOperationType } from './offlineQueue';
import { conflictResolution, ValidationResult } from './conflictResolution';
import axios from 'axios';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://kist-api-prod.mosbahsanad.workers.dev';

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2,
};

// Calculate retry delay with exponential backoff
function calculateRetryDelay(retryCount: number): number {
  const delay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
}

// Sync Service
export class SyncService {
  private isOnline: boolean = navigator.onLine;
  private isSyncing: boolean = false;
  private syncInterval: number | null = null;

  constructor() {
    this.setupNetworkListeners();
  }

  // Setup network status listeners
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.onNetworkRestored();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.onNetworkLost();
    });
  }

  // Handle network restoration
  private async onNetworkRestored(): Promise<void> {
    console.log('Network restored, starting sync...');
    await this.syncPendingItems();
  }

  // Handle network loss
  private onNetworkLost(): void {
    console.log('Network lost, pausing sync...');
    this.isSyncing = false;
  }

  // Check if online
  isNetworkOnline(): boolean {
    return this.isOnline;
  }

  // Check if currently syncing
  isCurrentlySyncing(): boolean {
    return this.isSyncing;
  }

  // Start periodic sync
  startPeriodicSync(intervalMs: number = 30000): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = window.setInterval(async () => {
      if (this.isOnline && !this.isSyncing) {
        await this.syncPendingItems();
      }
    }, intervalMs);
  }

  // Stop periodic sync
  stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Sync all pending items
  async syncPendingItems(): Promise<void> {
    if (this.isSyncing || !this.isOnline) {
      return;
    }

    this.isSyncing = true;
    console.log('Starting sync of pending items...');

    try {
      const operationTypes: QueueOperationType[] = ['create-agreement', 'post-payment', 'add-note'];
      
      for (const operationType of operationTypes) {
        await this.syncOperationType(operationType);
      }

      // Update last sync timestamp
      await offlineQueue.updateSyncStatus(Date.now());
      
      console.log('Sync completed successfully');
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  // Sync specific operation type
  private async syncOperationType(operationType: QueueOperationType): Promise<void> {
    const pendingItems = await offlineQueue.getPendingItems(operationType);
    
    for (const item of pendingItems) {
      try {
        await this.processQueueItem(item);
      } catch (error) {
        console.error(`Failed to process ${operationType} item ${item.id}:`, error);
        
        // Update item status to failed
        await offlineQueue.updateItemStatus(
          operationType,
          item.id,
          'failed',
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
    }
  }

  // Process individual queue item
  private async processQueueItem(item: QueueItem): Promise<void> {
    // Check retry limits
    if (item.metadata.retryCount >= RETRY_CONFIG.maxRetries) {
      await offlineQueue.updateItemStatus(
        item.operationType,
        item.id,
        'failed',
        'Max retries exceeded'
      );
      return;
    }

    // Validate item before processing
    const validation = conflictResolution.validateQueueItem(item, this.isOnline);
    if (!validation.isValid) {
      await offlineQueue.updateItemStatus(
        item.operationType,
        item.id,
        'failed',
        validation.error || 'Validation failed'
      );
      return;
    }

    // Update status to processing
    await offlineQueue.updateItemStatus(item.operationType, item.id, 'processing');

    try {
      // Send request to API
      await this.sendRequest(item);

      // Mark as completed and remove from queue
      await offlineQueue.updateItemStatus(item.operationType, item.id, 'completed');
      await offlineQueue.removeItem(item.operationType, item.id);

      console.log(`Successfully synced ${item.operationType} item ${item.id}`);
    } catch (error) {
      // Calculate retry delay
      const retryDelay = calculateRetryDelay(item.metadata.retryCount);
      
      console.log(`Retrying ${item.operationType} item ${item.id} in ${retryDelay}ms`);
      
      // Schedule retry
      setTimeout(async () => {
        if (this.isOnline) {
          await this.processQueueItem(item);
        }
      }, retryDelay);
    }
  }

  // Send request to API
  private async sendRequest(item: QueueItem): Promise<any> {
    const { url, method, data, headers } = item.payload;

    const config = {
      method,
      url: `${API_BASE_URL}${url}`,
      data,
      headers: {
        ...headers,
        'X-Idempotency-Key': item.idempotencyKey,
      },
    };

    return axios(config);
  }

  // Manual sync trigger
  async manualSync(): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    await this.syncPendingItems();
  }

  // Get sync status
  async getSyncStatus(): Promise<{
    isOnline: boolean;
    isSyncing: boolean;
    lastSynced: number | null;
    pendingCount: number;
  }> {
    const stats = await offlineQueue.getQueueStats();
    const pendingCount = Object.values(stats).reduce((sum, stat) => sum + stat.pending, 0);
    const lastSynced = await offlineQueue.getLastSyncTimestamp();

    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      lastSynced,
      pendingCount,
    };
  }

  // Add operation to queue with conflict validation
  async queueOperation(
    operationType: QueueOperationType,
    payload: any,
    idempotencyKey?: string
  ): Promise<QueueItem> {
    // Validate operation before queuing
    const validation = conflictResolution.validateOperation(operationType, payload, this.isOnline);
    
    if (!validation.isValid) {
      throw new Error(validation.error || 'Operation validation failed');
    }

    // Show warnings if any
    if (validation.warnings && validation.warnings.length > 0) {
      console.warn('Operation warnings:', validation.warnings);
    }

    return offlineQueue.addToQueue(operationType, payload, idempotencyKey);
  }

  // Validate operation without queuing
  validateOperation(
    operationType: QueueOperationType,
    payload: any
  ): ValidationResult {
    return conflictResolution.validateOperation(operationType, payload, this.isOnline);
  }

  // Check if operation should be blocked
  shouldBlockOperation(
    operationType: QueueOperationType,
    payload: any
  ): boolean {
    return conflictResolution.shouldBlockOperation(operationType, payload, this.isOnline);
  }

  // Get blocking reason
  getBlockingReason(
    operationType: QueueOperationType,
    payload: any
  ): string | null {
    return conflictResolution.getBlockingReason(operationType, payload, this.isOnline);
  }

  // Clean up
  destroy(): void {
    this.stopPeriodicSync();
  }
}

// Export singleton instance
export const syncService = new SyncService(); 